/**
 * Entity location formatting — ONE place that turns the server's
 * `EntityLocation` into the breadcrumb a row renders.
 *
 * Both surfaces that show "where does this live" call this: the global search
 * rows and the move/copy destination picker. They differ in where the string
 * is placed (beside the title vs. under it), never in what it says — which is
 * the whole point, since the divergence this closes was search resolving one
 * level of parent while the picker resolved nothing at all.
 *
 * The workspace name is supplied by the caller rather than the server: every
 * client already holds it, so sending it once per result row would be the same
 * string repeated twenty times per response.
 */

import type { EntityLocation } from '@bower/shared';

/** Between segments. Narrow spaces around the slash so a path reads as one unit. */
const SEPARATOR = ' / ';

/** Stands in for the part of the chain that isn't shown (or isn't readable). */
const ELLIPSIS = '…';

/**
 * How many parts a breadcrumb shows before the middle is elided. Four is the
 * point where a path stops being scannable at 12px in a 755px dialog — and the
 * two that always survive are the outermost (which workspace / root) and the
 * innermost (the immediate parent), because those are the two that
 * disambiguate two collections with the same name.
 */
const MAX_PARTS = 4;

export interface FormattedLocation {
  /** What the row renders — middle elided when the chain is long. */
  display: string;
  /** The whole chain, for a `title` tooltip. Equals `display` when nothing was elided. */
  full: string;
  /**
   * `display` split at the last separator, so a row can let the HEAD absorb the
   * clipping and keep the LEAF on screen.
   *
   * This is not cosmetic. A breadcrumb clipped from the right loses its last
   * segment — the immediate parent, the one segment that actually tells two
   * same-named collections apart — so at narrow widths the rows go back to
   * looking identical, which is the bug this whole feature exists to fix.
   * Measured in Chrome at a 420px viewport: the full path wrapped to three
   * lines inside a one-line clamp and rendered as "'s Workspace / CRISPR-Cas9
   * Knock-In…", with the leaf gone.
   *
   * `head` carries its own trailing separator, so `head + leaf === display`.
   */
  head: string;
  leaf: string;
}

/**
 * Format a location for display, or `null` when there is nothing to say.
 *
 * `null` covers two distinct cases that should both render as no breadcrumb:
 * the server sent no location (an artifact nested under another artifact, whose
 * container is not a collection), and the chain is entirely unreadable
 * (`truncated` with no segments — the shared-with-me case).
 */
export function formatEntityLocation(
  location: EntityLocation | undefined,
  workspaceName: string | undefined,
): FormattedLocation | null {
  if (!location) return null;

  const names = location.segments.map((segment) => segment.name);

  // Nothing in the chain is readable. A lone ellipsis is not a breadcrumb — it
  // is a row that looks like it is telling you something and isn't.
  if (names.length === 0 && location.truncated) return null;

  // A truncated chain leads with an ellipsis instead of the workspace name:
  // the walk never reached the root, so claiming it did would present a
  // partial path as a complete one.
  const root = location.truncated ? ELLIPSIS : workspaceName;
  if (!root && names.length === 0) return null;

  const parts = root ? [root, ...names] : names;
  if (parts.length === 0) return null;

  const full = parts.join(SEPARATOR);
  if (parts.length <= MAX_PARTS) return { display: full, full, ...split(parts) };

  // Keep the ends, drop the middle. `parts[0]` is already the ellipsis when the
  // chain was truncated server-side, so a doubly-shortened path reads
  // "… / Assay Development / Plate Reads" rather than stacking two ellipses.
  const first = parts[0];
  const tail = parts.slice(-2);
  const shown = first === ELLIPSIS ? [first, ...tail] : [first, ELLIPSIS, ...tail];
  return { display: shown.join(SEPARATOR), full, ...split(shown) };
}

/** Split rendered parts into a clippable head and the leaf that must survive. */
function split(parts: string[]): { head: string; leaf: string } {
  const leaf = parts[parts.length - 1] ?? '';
  const head = parts.length > 1 ? `${parts.slice(0, -1).join(SEPARATOR)}${SEPARATOR}` : '';
  return { head, leaf };
}
